// Read/write functions for Billing Settings → Firestore.
//
// Firestore layout:
//
//   organizations/{orgId}/billingSettings/lateFees
//
// These docs are owned by moxit-wonderschool-web. The enforcement sweep
// that reads `lateFees` is a moxit Cloud Function (PAYM-5076), NOT a Nova
// Oban worker — that architecture was rejected with ws-common #6331. Nova
// must never read Firestore for this feature.
// https://linear.app/wonderschool/issue/PAYM-5076/enforce-the-late-payment-policy-dunning-ladder-replacement-invoice
//
// One document. The emailTemplates doc was removed with the editable-template
// UI (PAYM-5075): the ladder copy is code-owned in defaults.ts, so there is
// nothing per-provider to store. Providers configure only which days reminders
// send, and those live on the policy.
//
// The doc absent / fields missing case is handled by `withDefaults()`
// helpers below — a school that has never edited their settings reads
// as if they're on the platform defaults, never as a `null` policy.
//
// Orgs that saved under an earlier commit of this branch may have an
// orphaned `organizations/{orgId}/billingSettings/emailTemplates` doc that
// nothing reads, writes, or deletes anymore. Decision: leave it — this
// branch is unmerged, so that's emulator/staging data only, and adding a
// one-time cleanup call for pre-merge branch churn isn't worth it.

import { getDocument, setDocument } from '../../api/firebase2/firestore';
import { DEFAULT_BILLING_SETTINGS, DEFAULT_LATE_FEE_POLICY } from './defaults';
import type { ApprovalMode, BillingSettingsData, FeeScope, LateFeePolicy, LateFeeType } from './types';

// ----- Path builders --------------------------------------------------------

const lateFeesPath = (orgId: string) => `organizations/${orgId}/billingSettings/lateFees`;

// ----- withDefaults() helpers ----------------------------------------------
//
// A doc absent from Firestore is the same as "school is on defaults".
// These helpers merge whatever's in Firestore over the platform defaults
// so the UI always renders a complete shape — never a `field undefined`
// crash on a partially-migrated doc.

// A spread merge would let a malformed stored value through — a doc with
// `approvalMode: 'banana'` would reach the UI and the enforcement sweep.
// These pick* helpers validate per field instead, so anything that isn't
// a recognised value falls back to the platform default.

const pickEnum = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback;

// Numeric values are finite and inside their allowed range. Rejects strings,
// NaN, Infinity, negatives and absurd magnitudes.
// Out of range is treated the same as malformed: fall back to the platform
// default rather than silently clamping a provider's saved number.
const pickNumber = (value: unknown, fallback: number, min = 0, max = Number.MAX_SAFE_INTEGER): number =>
  typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max ? value : fallback;

const pickInteger = (value: unknown, fallback: number, min = 0, max = Number.MAX_SAFE_INTEGER): number =>
  Number.isInteger(value) ? pickNumber(value, fallback, min, max) : fallback;

const pickBoolean = (value: unknown, fallback: boolean): boolean => (typeof value === 'boolean' ? value : fallback);

// Ranges the form must also enforce, kept here as the storage-layer backstop.
const MIN_GRACE_PERIOD_DAYS = 2; // 1 leaves no separate day for the "past due, no fee yet" email
const MAX_GRACE_PERIOD_DAYS = 30;
const MAX_PERCENT_FEE = 100;
// Business ceiling, not a validation artefact: 20x the $25 platform default,
// above any plausible fee but low enough that corrupt data cannot charge thousands.
const MAX_LATE_FEE_AMOUNT = 500;
const MIN_CONSECUTIVE_MONTHS = 1; // 0 reads as "policy on" while never charging anything
const MIN_REMINDER_DAYS_BEFORE = 1; // 0 duplicates the "due today" email
const MIN_FIRM_REMINDER_DAYS_AFTER = 1; // 0 would land the same day the invoice goes past due
const MIN_ESCALATION_DAYS_AFTER = 1;

const LATE_FEE_TYPES: readonly LateFeeType[] = ['flat', 'percent'];
const FEE_SCOPES: readonly FeeScope[] = ['per_invoice', 'per_family'];
const APPROVAL_MODES: readonly ApprovalMode[] = ['auto', 'review_queue'];

// Exported for unit tests in __tests__/api.test.ts.
// Production callers should still go through fetchBillingSettings().
export function policyWithDefaults(raw: Partial<LateFeePolicy> | null): LateFeePolicy {
  const d = DEFAULT_LATE_FEE_POLICY;
  if (!raw) return { ...d };
  // Resolved first: `lateFeeAmount` means percent or dollars depending on it,
  // and a $150 flat fee is legitimate where 150% is not.
  const lateFeeType = pickEnum(raw.lateFeeType, LATE_FEE_TYPES, d.lateFeeType);
  const hasInvalidLateFeeType =
    raw.lateFeeType !== undefined && !LATE_FEE_TYPES.includes(raw.lateFeeType as LateFeeType);
  const lateFeeAmount = pickNumber(
    hasInvalidLateFeeType ? undefined : raw.lateFeeAmount,
    d.lateFeeAmount,
    0,
    lateFeeType === 'percent' ? MAX_PERCENT_FEE : MAX_LATE_FEE_AMOUNT
  );
  let maxLateFee = pickNumber(raw.maxLateFee, d.maxLateFee, 0, MAX_LATE_FEE_AMOUNT);
  // The cap is hidden in v1, so it must follow an explicitly configured flat
  // fee rather than silently charging less than the amount shown in the UI.
  if (lateFeeType === 'flat' && lateFeeAmount > maxLateFee) maxLateFee = lateFeeAmount;
  // Field-by-field, so an unknown stored field can't ride along either.
  return {
    policyEnabled: pickBoolean(raw.policyEnabled, d.policyEnabled),
    lateFeeType,
    lateFeeAmount,
    maxLateFee,
    graceAmountThreshold: pickNumber(raw.graceAmountThreshold, d.graceAmountThreshold),
    feeScope: pickEnum(raw.feeScope, FEE_SCOPES, d.feeScope),
    // The money path: garbage here must never resolve to 'auto'.
    approvalMode: pickEnum(raw.approvalMode, APPROVAL_MODES, d.approvalMode),
    subsidyInvoicesExempt: pickBoolean(raw.subsidyInvoicesExempt, d.subsidyInvoicesExempt),
    remindersEnabled: pickBoolean(raw.remindersEnabled, d.remindersEnabled),
    maxConsecutiveMonths: pickInteger(raw.maxConsecutiveMonths, d.maxConsecutiveMonths!, MIN_CONSECUTIVE_MONTHS),
    ...reminderLadderWithDefaults(raw, d),
  };
}

// Validate each whole-day count, then check the ordered ladder. A stored
// schedule must keep grace < firm < escalation. The linked fields fall back
// together so the result stays internally consistent.
function reminderLadderWithDefaults(
  raw: Partial<LateFeePolicy>,
  d: LateFeePolicy
): Pick<LateFeePolicy, 'gracePeriodDays' | 'reminderDaysBefore' | 'firmReminderDaysAfter' | 'escalationDaysAfter'> {
  let gracePeriodDays = pickInteger(
    raw.gracePeriodDays,
    d.gracePeriodDays,
    MIN_GRACE_PERIOD_DAYS,
    MAX_GRACE_PERIOD_DAYS
  );
  const reminderDaysBefore = pickInteger(raw.reminderDaysBefore, d.reminderDaysBefore, MIN_REMINDER_DAYS_BEFORE);
  let firmReminderDaysAfter = pickInteger(
    raw.firmReminderDaysAfter,
    d.firmReminderDaysAfter,
    MIN_FIRM_REMINDER_DAYS_AFTER
  );
  let escalationDaysAfter = pickInteger(raw.escalationDaysAfter, d.escalationDaysAfter, MIN_ESCALATION_DAYS_AFTER);

  if (firmReminderDaysAfter <= gracePeriodDays || escalationDaysAfter <= firmReminderDaysAfter) {
    gracePeriodDays = d.gracePeriodDays;
    firmReminderDaysAfter = d.firmReminderDaysAfter;
    escalationDaysAfter = d.escalationDaysAfter;
  }

  return { gracePeriodDays, reminderDaysBefore, firmReminderDaysAfter, escalationDaysAfter };
}

// ----- Public API ----------------------------------------------------------

export async function fetchBillingSettings(organizationId: string): Promise<BillingSettingsData> {
  if (!organizationId) {
    return DEFAULT_BILLING_SETTINGS;
  }

  // One doc. A missing doc does NOT reject — getDocument resolves to `{}`
  // (id-only), which the withDefaults helpers treat as "no overrides yet".
  // A genuine read failure (permission-denied, offline, etc.) DOES reject,
  // and is left to propagate here so the caller's `.catch` can tell "never
  // configured" apart from "we couldn't read it" and show an error banner
  // instead of quietly rendering platform defaults as the org's policy.
  const policyRaw = await getDocument({ path: lateFeesPath(organizationId) });

  return {
    lateFeePolicy: policyWithDefaults(policyRaw as Partial<LateFeePolicy> | null),
  };
}

export async function saveLateFeePolicy(organizationId: string, policy: LateFeePolicy): Promise<LateFeePolicy> {
  if (!organizationId) {
    throw new Error('saveLateFeePolicy: organizationId is required');
  }

  const normalizedPolicy = policyWithDefaults(policy);

  // setDocument with merge=false writes the full shape every save.
  // Late fee policy is small (~12 fields) so this is fine; eliminates
  // the "leftover field from a previous schema version" footgun.
  await setDocument({
    path: lateFeesPath(organizationId),
    data: normalizedPolicy as unknown as Record<string, unknown>,
    merge: false,
  });
  return normalizedPolicy;
}
